import React from 'react';
import { Box, Button, EmptyState } from '@meddihub/ui';
import {
	HostPatient,
	MzcrServiceRequests,
	ServiceRequestsView,
} from '@meddihub/mzcr-ui';
import { getMzcrClient } from '../Services/mzcrClient';
import { useMzcrContext } from '../Services/useMzcrContext';
import useMzcrSettingsLink from '../Services/useMzcrSettingsLink';
import useRefreshMzcrAcl from '../Services/useRefreshMzcrAcl';
import useMzcrLocale from '../Services/useMzcrLocale';
import MzcrHostPatientSearch from './MzcrHostPatientSearch';
import { CloudOffIcon } from './icons';
// @ts-ignore
import styles from './MzcrServiceRequestsPage.module.scss';
import useTranslation from '../../../Hooks/useTranslation';
import ContentSkeleton from '../../Base/Components/ContentSkeleton';

interface Props {
	view: ServiceRequestsView;
	links: Record<ServiceRequestsView, string>;
	workplaceName?: string;
}

const renderHostPatientSearch = ({
	onSelect,
}: {
	onSelect: (patient: HostPatient) => void;
}) => <MzcrHostPatientSearch onSelect={onSelect} />;

const MzcrServiceRequestsPage: React.FC<Props> = ({
	view,
	links,
	workplaceName,
}) => {
	const { t } = useTranslation();
	const { onOpenCertificateSettings } = useMzcrSettingsLink();
	const refreshMzcrAcl = useRefreshMzcrAcl();
	const locale = useMzcrLocale();
	const mzcrContext = useMzcrContext();
	const changeView = (next: ServiceRequestsView) =>
		window.location.assign(links[next]);

	if (mzcrContext.isLoading) {
		return <ContentSkeleton />;
	}

	if (mzcrContext.isError || !mzcrContext.context) {
		return (
			<Box p={8}>
				<EmptyState
					tone='danger'
					icon={<CloudOffIcon />}
					title={t('component.mzcr.connectionFailed.title')}
					actions={
						<Button
							variant='link'
							onClick={mzcrContext.refetch}
							isLoading={mzcrContext.isRefetching}>
							{t('component.mzcr.retry')}
						</Button>
					}>
					{t('component.mzcr.connectionFailed.text')}
				</EmptyState>
			</Box>
		);
	}

	return (
		<div className={styles.panel}>
			<MzcrServiceRequests
				mzcrClient={getMzcrClient()}
				{...mzcrContext.context}
				organization={{ ...mzcrContext.context.organization, workplaceName }}
				view={view}
				onViewChange={changeView}
				renderHostPatientSearch={renderHostPatientSearch}
				onOpenCertificateSettings={onOpenCertificateSettings}
				locale={locale}
				onCertificateUploaded={refreshMzcrAcl}
			/>
		</div>
	);
};

export default MzcrServiceRequestsPage;
